import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type BadgeContent,
  type CalibrationLabels,
  printBadge as printBadgeInBrowser,
  printCalibrationLabel as printCalibrationInBrowser,
} from '../config/badgePrint';
import {
  fetchPrinterStatus,
  type JobPayload,
  LabelPrintError,
  loadPrinterChoice,
  type PrintAgent,
  type PrinterState,
  type PrinterStatusResponse,
  printerState,
  printViaAgent,
  savePrinterChoice,
  selectedAgent,
} from '../config/labelPrinter';

/** Ilyen sűrűn frissül a pult nyomtató-jelzője. */
export const PRINTER_STATUS_POLL_MS = 5000;

export interface LabelPrinter {
  /**
   * `zebra`: a szervezetnek van bekötött nyomtatója, a címke a sorba megy;
   * `browser`: nincs egy sem, a régi `window.print()` út.
   */
  mode: 'zebra' | 'browser';
  state: PrinterState;
  status: PrinterStatusResponse | null;
  /** A szervezet nyomtatói (ügynökei). */
  agents: PrintAgent[];
  /** A nyomtató, amelyre EZ a gép nyomtat. */
  agent: PrintAgent | null;
  select: (agentId: string) => void;
  refresh: () => Promise<void>;
  printBadge: (content: BadgeContent, ticketId?: string) => Promise<void>;
  printTestLabel: (content: BadgeContent) => Promise<void>;
  printCalibration: (labels: CalibrationLabels) => Promise<void>;
}

/**
 * A pult nyomtatója (#178).
 *
 * KÉT NYOMTATÓ A PULTON. A QR-pult gépei az egyikre, a kézi pult gépei a
 * másikra nyomtatnak, ezért minden gép kiválasztja a SAJÁT nyomtatóját, és a
 * böngésző megjegyzi. Egyetlen nyomtatónál nincs mit választani.
 *
 * NINCS CSENDES VISSZAESÉS A BÖNGÉSZŐRE, ha van bekötött nyomtató. A pult
 * Windowsos gépein nincs rendszernyomtató: a böngésző nyomtatási ablaka csak
 * összezavarná a kezelőt, a címke pedig nem jönne ki. Offline vagy hibás
 * nyomtatónál ezért azonnal, érthetően szólunk. Böngészős nyomtatás csak
 * akkor megy, ha a szervezetnek egyáltalán nincs nyomtatója.
 *
 * Mindhárom függvény ugyanazt a szerződést tartja, mint a böngészős
 * `printBadge`: sikerrel csak akkor tér vissza, ha a címke kiment, különben
 * dob. A pult erre építi, hogy csak sikeres nyomtatás után léptet be.
 */
export function useLabelPrinter(eventId: string | undefined): LabelPrinter {
  const [status, setStatus] = useState<PrinterStatusResponse | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(() => loadPrinterChoice());
  // A kérés válaszát csak akkor írjuk be, ha közben nem váltottak eseményt.
  const eventRef = useRef(eventId);
  eventRef.current = eventId;

  const refresh = useCallback(async () => {
    const requested = eventId;
    if (!requested) {
      setStatus(null);
      return;
    }
    try {
      const next = await fetchPrinterStatus(requested);
      if (eventRef.current === requested) setStatus(next);
    } catch {
      // A LEGUTÓBBI ISMERT ÁLLAPOT MARAD. Egy elvesztett lekérdezésre nullázva
      // a képernyő „nincs nyomtató"-ra esett volna vissza, és onnan a böngésző
      // nyomtatási ablakára — a pult Windowsos gépein rendszernyomtató nélkül.
      // A nyomtatás úgyis a backenden át megy: ha az nem él, ott hibázik.
    }
  }, [eventId]);

  useEffect(() => {
    setStatus(null);
    if (!eventId) return undefined;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      await refresh();
      if (!cancelled) timer = setTimeout(tick, PRINTER_STATUS_POLL_MS);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [eventId, refresh]);

  const select = useCallback((agentId: string) => {
    setSelectedId(agentId);
    savePrinterChoice(agentId);
  }, []);

  const agents = status?.agents ?? [];
  const agent = selectedAgent(status, selectedId);
  // Amíg az első lekérdezés meg nem jött, NEM tudjuk, van-e nyomtató. Ilyenkor
  // se nem „Zebra", se nem „böngésző": a nyomtatás megvárja az állapotot.
  const loaded = status !== null;
  const state = loaded ? printerState(status, selectedId) : 'unknown';
  const zebra = !!eventId && (!loaded || agents.length > 0);

  /**
   * A Zebra útja. Nem kész nyomtatónál AZONNAL hibázunk: a szerver ilyen
   * nyomtatónak úgysem ad munkát, a gomb különben 15 másodpercig várna.
   */
  const viaZebra = useCallback(
    async (payload: JobPayload) => {
      if (!eventId) return;
      if (!loaded) throw new LabelPrintError('notReady');
      if (!agent) throw new LabelPrintError('noPrinterSelected');
      if (state !== 'ready') throw new LabelPrintError('notReady');
      await printViaAgent(eventId, agent.id, payload);
    },
    [eventId, agent, state, loaded]
  );

  const printBadge = useCallback(
    async (content: BadgeContent, ticketId?: string) => {
      if (zebra) await viaZebra({ kind: 'BADGE', badge: content, ticketId });
      else await printBadgeInBrowser(content);
    },
    [zebra, viaZebra]
  );

  /**
   * A Zebra tesztcímkéjén a NYOMTATÓ NEVE áll a név helyén („ZD230 Wifi”).
   * Ha a „ZD230 Wifi” feliratú címke a LAN-os nyomtatóból jön ki, egy
   * pillantásra látszik, hogy a két IP-cím fel van cserélve — a jelző ezt nem
   * tudja, mert mindkét nyomtató valódi és kész.
   */
  const printTestLabel = useCallback(
    async (content: BadgeContent) => {
      if (zebra) {
        await viaZebra({ kind: 'TEST', badge: { ...content, name: agent?.name || content.name } });
      } else {
        await printBadgeInBrowser(content);
      }
    },
    [zebra, viaZebra, agent]
  );

  const printCalibration = useCallback(
    async (labels: CalibrationLabels) => {
      if (zebra) await viaZebra({ kind: 'CALIBRATION', calibration: labels });
      else await printCalibrationInBrowser(labels);
    },
    [zebra, viaZebra]
  );

  return {
    mode: zebra ? 'zebra' : 'browser',
    state,
    status,
    agents,
    agent,
    select,
    refresh,
    printBadge,
    printTestLabel,
    printCalibration,
  };
}
